import React, { useEffect, useState } from 'react';
import { Divider, Tag } from '@fx-ui/fine-design';
import { getMemberCardDetail } from '../../apis';
import {
    compact,
    ECode,
    ExtValue,
    filter,
    formatDeptPath,
    formatMemberField,
    get,
    has,
    MemberFieldApiData,
    includes,
    isEmpty,
    isNil,
    isObjectEmpty,
    map,
    MemberCardView,
    PresetMemberFieldKey,
    MemberType,
    ValueOf,
    MemberField,
    MemberFieldValueType,
    parseDate,
    transformDayjsFormat,
    join,
    makeArray,
    SEP,
    size,
    pick,
    DeptStatus,
    DeptType,
    MemberFieldSource
} from '@libs/shared';
import MemberAvatar from '../member-avatar';
import MemberName from '../member-name';
import { isResignedMember } from '../../utils';
import { authModel } from '../../models';
import { platformIntl } from '../../constants';
import classNames from 'classnames';

interface MemberCardProps {
    memberId?: string;
    className?: string;
    tenantId?: string;
}

interface TableDataItem {
    key: string;
    label: string;
    value:
        | ValueOf<Pick<MemberCardView, 'username' | 'phone' | 'email' | 'dept_path' | 'roles'>>
        | ExtValue;
    showValue: React.ReactNode;
}

const needCheckValueEmpty = (fieldKey: string) => {
    return !includes(['nickname', 'username', 'dept'], fieldKey);
};

/**
 * 成员信息卡片
 */
const MemberCard: React.FC<MemberCardProps> = (props) => {
    const { memberId, className, tenantId } = props;

    const safeTenantId = authModel.tenantId || tenantId || '';
    // {} 展示无法查看成员信息的提示
    const [viewInfo, setViewInfo] = useState<MemberCardView | {}>();
    const [memberFields, setMemberFields] = useState<MemberFieldApiData[]>();

    const isMemberFieldShow = (fieldKey: string) => {
        // 部门字段实际从dept_path参数取值
        const validKey = fieldKey === 'dept' ? 'dept_path' : fieldKey;
        // 不可见字段后台直接不返回，避免越权
        return has(viewInfo, validKey) || has((viewInfo as MemberCardView)?.ext, validKey);
    };

    useEffect(() => {
        fetchMemberInfo();
    }, []);

    const fetchMemberInfo = () => {
        if (!memberId || !safeTenantId) {
            return;
        }
        getMemberCardDetail(memberId, safeTenantId)
            .then((res) => {
                const member = res.data;
                if (isResignedMember(member.status)) {
                    setViewInfo({});
                } else {
                    setViewInfo(member);
                }
                setMemberFields(res.fields);
            })
            .catch((res) => {
                const err = get(res, 'response.data');
                if (
                    includes(
                        [
                            ECode.CORP_MEMBER_NOT_EXISTS,
                            ECode.DEPT_NOT_FOUND,
                            ECode.MEMBER_NOT_JOINED_TENANT,
                            ECode.MEMBER_IS_NOT_ACTIVE
                        ],
                        err.code
                    )
                ) {
                    // 企业成员不存在
                    setViewInfo({});
                    setMemberFields([]);
                }
            });
    };

    if (isNil(viewInfo) || isNil(memberFields)) {
        return null;
    }

    const renderMemberFieldShowValue = (field: MemberField, value?: any) => {
        switch (field.valueType) {
            case MemberFieldValueType.Datetime: {
                const date = parseDate(value, authModel.timezone);
                const fmt = transformDayjsFormat(field.format || 'yyyy-MM-dd');
                return isNil(date) ? '' : date.format(fmt);
            }
            case MemberFieldValueType.User:
            case MemberFieldValueType.UserGroup: {
                const valueList = makeArray(value);
                return (
                    <>
                        {map(valueList, (item, index) => {
                            const isLast = index + 1 === size(valueList);
                            return (
                                <React.Fragment key={index}>
                                    <MemberName
                                        name={item.nickname ?? ''}
                                        {...pick(item, ['status', 'type'])}
                                    />
                                    {!isLast && <span>{SEP.COMMA}</span>}
                                </React.Fragment>
                            );
                        })}
                    </>
                );
            }
            case MemberFieldValueType.Dept:
            case MemberFieldValueType.DeptGroup:
                return join(
                    map(makeArray(value), (item) => {
                        if (!item.name) {
                            return '';
                        }
                        if (
                            item.status === DeptStatus.dismission &&
                            item.type === DeptType.corp_coop
                        ) {
                            return `${item.name}${platformIntl.get('corp.removed.suffix')}`;
                        }
                        return item.name;
                    }),
                    SEP.COMMA
                );
            case MemberFieldValueType.Combo:
            case MemberFieldValueType.ComboCheck:
                return join(makeArray(value), SEP.COMMA);
            case MemberFieldValueType.Text:
            case MemberFieldValueType.Number:
            default:
                return value ?? '';
        }
    };

    const renderMemberInfo = () => {
        if (isEmpty(viewInfo)) {
            return <div className="card-content-empty">{platformIntl.get('member.empty.tip')}</div>;
        }
        const { nickname, avatar, status, type, phone, email, roles, username, dept_path, ext } =
            viewInfo as MemberCardView;
        const aliasValue = get(ext, PresetMemberFieldKey.Alias);
        const aliasShowValue = (aliasValue ?? '') as string;
        const roleShowValue = map(roles, 'name').join(platformIntl.get('symbol.pause'));
        const tableData: TableDataItem[] = compact([
            {
                key: 'username',
                label: platformIntl.get('username'),
                value: username,
                showValue: username
            },
            {
                key: 'phone',
                label: platformIntl.get('phone'),
                value: phone,
                showValue: phone
            },
            {
                key: 'email',
                label: platformIntl.get('email'),
                value: email,
                showValue: email
            },
            type === MemberType.external && {
                key: 'roles',
                label: platformIntl.get('role'),
                value: roles,
                showValue: (
                    <span className="roles-name" title={roleShowValue}>
                        {roleShowValue}
                    </span>
                )
            },
            type !== MemberType.external && {
                key: 'dept',
                label: platformIntl.get('department'),
                value: dept_path,
                showValue: formatDeptPath({
                    hasCurrentDept: true,
                    deptPath: dept_path,
                    corpName: authModel.currentCorpName
                })
            },
            ...map(
                map(
                    filter(memberFields, (field) => {
                        return (
                            includes(
                                [MemberFieldSource.Preset, MemberFieldSource.Custom],
                                field.source
                            ) && field.widget_name !== PresetMemberFieldKey.Alias
                        );
                    }),
                    formatMemberField
                ),
                (field) => {
                    const fieldValue = get(ext, field.key);
                    return {
                        key: field.key,
                        label: field.label,
                        value: fieldValue,
                        showValue: renderMemberFieldShowValue(field, fieldValue)
                    };
                }
            )
        ]);
        const checkVisible = (fieldKey: string, value: any) => {
            return (
                isMemberFieldShow(fieldKey) &&
                (!needCheckValueEmpty(fieldKey) || !isObjectEmpty(value))
            );
        };

        const getTagText = () => {
            switch (type) {
                case MemberType.corp_coop:
                    return platformIntl.get('fxp.base.bg.extra_member.opt');
                case MemberType.external:
                    return platformIntl.get('fxp.base.bg.extra_user.ttl');
                case MemberType.internal:
                    return platformIntl.get('fxp.base.bg.intra_member.opt');
                default:
                    return '';
            }
        };

        const validTableData = filter(tableData, (item) => checkVisible(item.key, item.value));
        return (
            <div className="card-content">
                <div className="content-header">
                    <MemberAvatar className="avatar" size={40} name={nickname} img={avatar} />
                    <div className="text">
                        <div className="name">
                            <MemberName name={nickname} status={status} type={type} />
                        </div>
                        {checkVisible(PresetMemberFieldKey.Alias, aliasValue) && (
                            <div className="alias">
                                <span title={aliasShowValue}>{aliasShowValue}</span>
                            </div>
                        )}
                        <div className="type">
                            <Tag size="small" className="type-tag" shape="roundedRec">
                                {getTagText()}
                            </Tag>
                        </div>
                    </div>
                </div>
                {!isEmpty(validTableData) && (
                    <>
                        <Divider style={{ margin: '8px 0' }} />
                        <div className="content-info">
                            <table className="content-table">
                                <tbody>
                                    {map(validTableData, (item) => {
                                        return (
                                            <tr key={item.key}>
                                                <td>
                                                    <div className="item-label">
                                                        <span className="label-text">
                                                            {item.label}
                                                        </span>
                                                        <div className="label-text-placeholder">
                                                            {item.label}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="item-value">
                                                        {item.showValue}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        );
    };

    return <div className={classNames('fx-member-card', className)}>{renderMemberInfo()}</div>;
};

export default MemberCard;
